import { Fragment } from 'react';
import { TOUCH_TARGET } from './touch-target';
import { Link, useLocation } from 'react-router-dom';
import { HomeIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n';
import { folderLinkFor, pathToTab, tabToPath } from '../gallery/url-state';
import { homeLinkTab } from '../gallery/tab-pref';
import { folderPathSegments } from '../gallery/folder-path';
import type { GalleryTab } from '../gallery/state';
import { usePageResetTrigger } from './page-reset';

/**
 * Fil d'Ariane de l'en-tête authentifié — DÉRIVÉ DE LA ROUTE (`useLocation`).
 *
 * Reflète la hiérarchie de la page courante en segments cliquables séparés par
 * « / », précédés d'une icône Maison (retour accueil). Le dernier segment (page
 * courante) est LUI AUSSI cliquable — il pointe vers le chemin de BASE PROPRE de sa
 * page (query-string retirée) et porte toujours `aria-current="page"` (valide sur un
 * lien) : cliquer dessus « revient à l'état initial de la page ». Pour les écrans
 * pilotés par l'URL (galerie : tri/filtres/recherche), naviguer vers la base propre
 * suffit à tout réinitialiser. Pour les écrans à état React LOCAL (ex. revue de
 * dédoublonnage), un clic « même chemin » ne remonte pas le composant → on déclenche
 * EN PLUS le rappel `usePageReset` enregistré par la page (cf. `components/page-reset`).
 *
 * Il y a TOUJOURS un chemin visible : la Maison est TOUJOURS un lien cliquable avec
 * son libellé « Accueil » VISIBLE, et chaque route porte au moins un segment de
 * contexte après elle (routes galerie incluses : « Galerie », « Dossiers », « Tags »).
 * Sur `/dossiers?dossier=<X>` un 3e maillon
 * nomme le dossier sélectionné (chaîne vide = « Racine ») ; sur `/tags?tag=<uuid>`
 * on N'ajoute PAS de 3e maillon (l'id de tag est opaque — le tag reste visible via
 * sa puce de filtre) → on reste sur « Accueil / Tags ». La visionneuse
 * (`/photo/:id`) est un overlay qui recouvre l'en-tête (fil non visible) → repli
 * inoffensif sur « Accueil / Galerie ».
 *
 * LA MAISON MÈNE AU DERNIER ONGLET VISITÉ (XPHOTO-32, lot 1), pas en dur à `/` : elle
 * cible `tabToPath(homeLinkTab(pathname))` (cf. `gallery/tab-pref.ts`), toujours un
 * chemin de BASE PROPRE — donc `/dossiers` = racine des dossiers, jamais un
 * sous-dossier ni une query. Son libellé et son `aria-label` restent « Accueil », et la
 * sémantique du fil ne bouge pas : « Accueil / Dossiers » reste juste. La préférence
 * n'agit QUE sur cette cible — aucune redirection, aucun `navigate()` d'effet : qui
 * arrive sur `/` y reste (l'URL est la source de vérité, cf. régression v1.21.3).
 *
 * Le mapping segment de route → libellé i18n est explicite et typé (`MessageKey`) :
 * aucune clé fantôme, aucun texte en dur. Les libellés d'onglet réutilisent la
 * section i18n `nav.*` (mêmes libellés que la rangée d'icônes `GalleryTabsBar`).
 * Une route inconnue retombe proprement sur « Accueil / Galerie ».
 *
 * À NE PAS CONFONDRE avec `diagnostics/RouteBreadcrumbs` : celui-ci est un
 * mécanisme SÉPARÉ de capture d'activité (« Signaler un bug »), sans rendu.
 */

/**
 * Un maillon du fil. Le libellé est soit une clé i18n (`labelKey`), soit un texte
 * DÉJÀ résolu (`labelText`, pour les valeurs dynamiques comme un nom de dossier —
 * jamais du texte en dur, mais une donnée utilisateur). `to` = chemin de BASE PROPRE
 * du maillon (query-string retirée). Le DERNIER maillon (page courante) porte lui
 * aussi un `to` = le chemin de base propre de sa page → cliquer dessus revient à
 * l'état INITIAL de la page (cf. `trailFor`) tout en gardant `aria-current="page"`.
 */
interface Crumb {
  labelKey?: MessageKey;
  labelText?: string;
  /** Chemin de base propre du maillon (toujours présent : le maillon courant est cliquable). */
  to: string;
}

/** Libellé i18n d'onglet de galerie (même section `nav.*` que `GalleryTabsBar`). */
const TAB_LABEL_KEY: Record<GalleryTab, MessageKey> = {
  gallery: 'nav.gallery',
  folders: 'nav.folders',
  tags: 'nav.tags',
};

/**
 * Construit la liste des maillons INTERMÉDIAIRES (hors Maison, rendue à part) à
 * partir du chemin ET de la query. La Maison (toujours un lien, vers l'onglet
 * mémorisé) est le 1er maillon ; chaque route ajoute au moins un segment de contexte
 * après elle.
 */
function trailFor(pathname: string, search: URLSearchParams): Crumb[] {
  // Nettoyage EXIF (effacement).
  if (pathname === '/outils/exif') {
    return [{ labelKey: 'breadcrumb.exifTool', to: '/outils/exif' }];
  }

  // Publier des tags vers EXIF (outil séparé, split v0.53.0).
  if (pathname === '/outils/exif-publier') {
    return [{ labelKey: 'breadcrumb.exifPublishTool', to: '/outils/exif-publier' }];
  }

  // Dédoublonnage.
  if (pathname === '/outils/dedup') {
    return [{ labelKey: 'breadcrumb.dedupTool', to: '/outils/dedup' }];
  }

  // Gestion de la taxonomie (T5 lot B).
  if (pathname === '/outils/taxonomie') {
    return [{ labelKey: 'breadcrumb.taxonomy', to: '/outils/taxonomie' }];
  }

  // Console SUPERADMIN / SYSTÈME : /superadmin/:section (Général / Comptes / Liens d'accès /
  // Clés d'API / Journaux / Configuration LLM / Journal des appels IA / Maintenance). Testé
  // AVANT `/admin` (préfixes disjoints). Chaque section a son maillon (parité avec les onglets).
  if (pathname.startsWith('/superadmin')) {
    const section = pathname.split('/')[2];
    const sectionKey: MessageKey | undefined =
      section === 'general'
        ? 'breadcrumb.adminGeneral'
        : section === 'comptes'
          ? 'breadcrumb.adminUsers'
          : section === 'liens'
            ? 'breadcrumb.sessionLinks'
            : section === 'cles-api'
              ? 'breadcrumb.apiKeys'
              : section === 'journaux'
                ? 'breadcrumb.adminLogs'
                : section === 'llm'
                  ? 'breadcrumb.llmConfig'
                  : section === 'journal-ia'
                    ? 'breadcrumb.aiTrace'
                    : section === 'reinitialiser'
                      ? 'breadcrumb.adminReset'
                      : undefined;
    if (!sectionKey) return [{ labelKey: 'breadcrumb.superadmin', to: '/superadmin/comptes' }];
    return [
      { labelKey: 'breadcrumb.superadmin', to: '/superadmin/comptes' },
      { labelKey: sectionKey, to: `/superadmin/${section}` },
    ];
  }

  // Admin de GALERIE : /admin/:section (Accès / Erreurs de fichiers / Poubelle /
  // Maintenance). Chaque section a son propre chemin de base propre.
  if (pathname.startsWith('/admin')) {
    const section = pathname.split('/')[2];
    const sectionKey: MessageKey | undefined =
      section === 'acces'
        ? 'breadcrumb.adminGrants'
        : section === 'erreurs'
          ? 'breadcrumb.adminFileErrors'
          : section === 'absents'
            ? 'breadcrumb.adminMissingFiles'
            : section === 'poubelle'
              ? 'breadcrumb.adminTrash'
              : section === 'reinitialiser'
                ? 'breadcrumb.adminReset'
                : undefined;
    // /admin nu → un seul maillon « Admin » (cliquable vers sa base).
    if (!sectionKey) return [{ labelKey: 'breadcrumb.admin', to: '/admin/acces' }];
    return [
      { labelKey: 'breadcrumb.admin', to: '/admin/acces' },
      { labelKey: sectionKey, to: `/admin/${section}` },
    ];
  }

  // Réglages : /parametres/:section.
  if (pathname.startsWith('/parametres')) {
    const section = pathname.split('/')[2];
    const sectionKey: MessageKey | undefined =
      section === 'compte'
        ? 'breadcrumb.settingsAccount'
        : section === 'apparence'
          ? 'breadcrumb.settingsAppearance'
          : undefined;
    if (!sectionKey) return [{ labelKey: 'breadcrumb.settings', to: '/parametres/compte' }];
    return [
      { labelKey: 'breadcrumb.settings', to: '/parametres/compte' },
      { labelKey: sectionKey, to: `/parametres/${section}` },
    ];
  }

  // Routes galerie (`/`, `/dossiers`, `/tags`) et visionneuse
  // (`/photo/:id`, qui retombe sur l'onglet Galerie). L'onglet vient du CHEMIN.
  const tab = pathToTab(pathname);
  const tabPath = tabToPath(tab);

  // Dossiers avec un dossier sélectionné (`?dossier=<X>`) → un maillon par SEGMENT
  // ancêtre (navigation façon explorateur : « Dossiers / 2024 / vacances / plage »).
  // Chaque segment pointe vers son dossier ancêtre (`?dossier=<chemin>`) — le maillon
  // courant (dernier segment) vers son propre chemin. La racine (`?dossier=` vide) est
  // représentée par le maillon « Dossiers » lui-même (aucun segment ajouté).
  if (tab === 'folders') {
    const folder = search.get('dossier');
    if (folder !== null && folder !== '') {
      return [
        { labelKey: 'nav.folders', to: '/dossiers' },
        ...folderPathSegments(folder).map((seg) => ({
          labelText: seg.name,
          to: folderLinkFor(seg.path),
        })),
      ];
    }
  }

  // Tags : l'id de tag (`?tag=<uuid>`) est OPAQUE — on n'ajoute pas de 3e maillon
  // illisible (le tag reste visible via sa puce de filtre) → « Accueil / Tags ».

  // Onglet seul (Galerie / Dossiers sans dossier / Tags / À traiter) : maillon courant
  // cliquable vers le CHEMIN PROPRE de l'onglet (query retirée → filtres/tri réinitialisés).
  return [{ labelKey: TAB_LABEL_KEY[tab], to: tabPath }];
}

export function Breadcrumb(): React.JSX.Element {
  const { t } = useI18n();
  const location = useLocation();
  const triggerPageReset = usePageResetTrigger();
  const search = new URLSearchParams(location.search);
  const trail = trailFor(location.pathname, search);
  // Cible de la Maison : le dernier onglet visité, en chemin de BASE PROPRE (sans query).
  const homeTo = tabToPath(homeLinkTab(location.pathname));

  return (
    <nav aria-label={t('breadcrumb.label')} className="flex min-w-0 items-center">
      <ol className="flex min-w-0 items-center gap-1 text-sm text-muted">
        {/* Maillon Maison — TOUJOURS un lien, vers le dernier ONGLET visité (base propre,
            sans query). Le libellé « Accueil » est visible dès `sm` ; en MOBILE seule
            l'icône reste (allègement de l'en-tête), le lien gardant son nom accessible
            via `aria-label`. */}
        <li className="flex shrink-0 items-center">
          <Link
            to={homeTo}
            aria-label={t('breadcrumb.home')}
            className={`inline-flex ${TOUCH_TARGET} items-center justify-center gap-1 rounded-md px-1.5 py-1 hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand`}
          >
            <HomeIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span className="hidden truncate sm:inline">{t('breadcrumb.home')}</span>
          </Link>
        </li>

        {/* Maillons intermédiaires + page courante. */}
        {trail.map((crumb, index) => {
          const isLast = index === trail.length - 1;
          const label = crumb.labelKey ? t(crumb.labelKey) : (crumb.labelText ?? '');
          return (
            <Fragment key={crumb.labelKey ?? `text:${index}:${crumb.labelText ?? ''}`}>
              {/* Séparateur « / » — décoratif (le balisage <ol> porte déjà la sémantique). */}
              <li aria-hidden="true" className="shrink-0 select-none text-muted">
                /
              </li>
              <li className="flex min-w-0 items-center">
                {isLast ? (
                  // Maillon COURANT : cliquable (revient à l'état initial de la page)
                  // ET garde `aria-current="page"` (valide sur un lien). On déclenche
                  // en plus le reset local éventuel (pages à état React, cf. page-reset)
                  // — le clic « même chemin » ne remonterait sinon pas le composant.
                  <Link
                    to={crumb.to}
                    aria-current="page"
                    onClick={triggerPageReset}
                    className={`inline-flex ${TOUCH_TARGET} items-center truncate rounded-md px-1.5 py-1 font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand`}
                  >
                    {label}
                  </Link>
                ) : (
                  <Link
                    to={crumb.to}
                    className={`inline-flex ${TOUCH_TARGET} items-center truncate rounded-md px-1.5 py-1 hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand`}
                  >
                    {label}
                  </Link>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
